/**
 * A visit's margin: the chip on its header and the popover it opens
 * (`docs/quote-build-refresh.md` §7.1–§7.3, boards qb-04 and qb-09 · 2).
 *
 * The popover is `EditorShell` at 390, which is a bottom sheet on a phone, so
 * one component is the desktop popover, the iPad popover and the phone sheet.
 * On a desktop and an iPad it hangs under the chip with its right edge on the
 * chip's (qb-04: the box at `right: 44` of a card whose chip ends 44 in, the
 * nub 55 from its right edge — the chip's middle), and the page is not dimmed.
 * Its body is the shared `MarginPanel` with the visit's own two blocks behind
 * a Base only · With options control. Props only, so step 8 moves it into
 * `builder/margin/` unchanged.
 */
import { useState } from 'react'
import type { QuoteVisitView } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { formatNumber } from '../../lib/format.js'
import { usePhone } from '../../lib/use-narrow.js'
import { EditorShell, SHEET_BUTTON } from '../editor-shell.js'
import { Button, Segmented } from '../../components/primitives.js'
import { IconChevronDown } from '../../frame/icons.js'
import { InternalChip, MarginPanel } from './margin-panel.js'
import {
  CHIP_MARGIN, DONE, MARGIN_FACTS, OPEN_MARGIN, OPTIONAL_VISIT_NOTE, VISIT_SCOPE_LABELS, WHOLE_QUOTE,
} from '../copy.js'

/** `margin 40.1% ▾` — green at or over the target, amber under (§7.1). */
export function VisitMarginChip({ pct, targetPct, open, onOpen, label }: {
  pct: number | null
  targetPct: number
  open: boolean
  onOpen: (anchor: HTMLElement) => void
  label: string
}) {
  const under = pct !== null && pct < targetPct
  return (
    <button
      type="button"
      aria-label={label}
      aria-expanded={open}
      data-margin-chip
      onClick={(event) => onOpen(event.currentTarget)}
      /* 24px to see, a 44px hit area on a touch screen (§10.2). */
      className={cn('relative inline-flex h-6 items-center gap-1 whitespace-nowrap rounded-tile-small border px-2',
        'text-[11px] font-medium text-text-muted',
        'before:absolute before:-inset-x-1 before:-inset-y-2.5 before:content-[""] [@media(pointer:fine)]:before:hidden',
        under ? 'border-warn/40 bg-warn-bg' : open ? 'border-accent-line bg-accent-soft' : 'border-line bg-surface')}
    >
      {CHIP_MARGIN}
      <b className={cn('tnum', under ? 'text-warn' : 'text-accent-dark')}>
        {pct === null ? '—' : `${pct.toFixed(1)}%`}
      </b>
      <IconChevronDown size={12} className={cn('transition-transform', open && 'rotate-180')} />
    </button>
  )
}

/** qb-04's header tile: 32px, radius 10, accent-soft with the visit's capsule glyph. */
function SmallVisitTile() {
  return (
    <span aria-hidden className="flex h-8 w-8 shrink-0 items-center justify-center rounded-field
                                 bg-accent-soft text-accent">
      <svg viewBox="0 0 24 24" width={17} height={17} fill="none" stroke="currentColor"
        strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="9" width="18" height="6" rx="3" /><path d="M9 9v6M15 9v6" />
      </svg>
    </span>
  )
}

export function VisitMarginPopover({
  visit, anchor, onClose, targetPct, wholeQuotePct, onOpenMargin,
}: {
  visit: QuoteVisitView
  anchor: HTMLElement | null
  onClose: () => void
  targetPct: number
  wholeQuotePct: number | null
  onOpenMargin: () => void
}) {
  const phone = usePhone()
  /*
   * Closing hands focus back to the chip (§11 test 12), so Esc leaves the
   * keyboard where it was. A click elsewhere still wins: the browser moves
   * focus to what was pressed after this runs.
   */
  const close = () => {
    anchor?.focus({ preventScroll: true })
    onClose()
  }
  const optional = visit.margin.base === null
  const [scope, setScope] = useState<'base' | 'withOptions'>(optional ? 'withOptions' : 'base')
  const block = scope === 'base' && visit.margin.base ? visit.margin.base : visit.margin.withOptions

  const trips = Number.isFinite(visit.occurrences) ? visit.occurrences : null
  const perVisit = trips && trips > 0 ? visit.hours / trips : visit.hours
  const hours = `${formatNumber(Math.round(perVisit * 10) / 10)} h`
  const count = optional ? 'Optional visit' : trips === 1 || trips === null ? 'One visit' : `${trips} visits`
  const facts = optional
    ? `${count} · ${hours}`
    : `${count} · ${hours} · ${scope === 'base' ? 'base only' : 'with its option'}`

  /*
   * Lowest lines answer "which line is dragging it down", so they are the
   * lines under the target (qb-04 case 1 lists one). The section is hidden
   * only when nothing is under the target and neither is the visit (§7.2).
   */
  const lowest = visit.margin.lowest.filter((line) => line.marginPct !== null && line.marginPct < targetPct)
  const visitUnder = block.marginPct !== null && block.marginPct < targetPct

  return (
    <EditorShell
      open
      onClose={close}
      anchor={anchor}
      width={390}
      tone="internal"
      nub
      label={visit.name}
      header={phone
        ? (
          <div className="flex min-w-0 flex-1 flex-col">
            <b className="truncate text-[16px] font-bold text-text">{visit.name}</b>
            <span className="text-[12px] text-text-muted">{MARGIN_FACTS(count.toLowerCase(), hours)}</span>
          </div>
        )
        : (
        <>
          <SmallVisitTile />
          <div className="flex min-w-0 flex-col gap-px">
            <b className="truncate text-[14px] font-bold text-text">{visit.name}</b>
            <span className="truncate text-[12px] text-text-muted">{facts}</span>
          </div>
          <span className="ml-auto"><InternalChip /></span>
        </>
        )}
      footer={phone
        /* qb-09 · 2: the sheet's one 48px Done, white with a rule — the footer every phone sheet wears. */
        ? <Button className={cn(SHEET_BUTTON, 'w-full !text-[15px]')} onClick={close}>{DONE}</Button>
        : (
          <>
            <span className="text-text-muted">
              {WHOLE_QUOTE}{' '}
              <b className="tnum text-text">{wholeQuotePct === null ? '—' : `${wholeQuotePct.toFixed(1)}%`}</b>
            </span>
            <button type="button" onClick={onOpenMargin}
              className="ml-auto font-semibold text-accent hover:text-accent-dark">
              {OPEN_MARGIN}
            </button>
          </>
        )}
    >
      <div data-visit-margin className="flex flex-col">
        {optional
          ? (
            <p className="m-0 mb-3.5 rounded-field bg-info-bg px-3 py-2.5 text-[12.5px] leading-[1.45] text-info">
              {OPTIONAL_VISIT_NOTE}
            </p>
          )
          : (
            <div className="mb-3.5">
              <Segmented
                full
                size="large"
                label="Visit margin scope"
                value={scope}
                onChange={setScope}
                options={[
                  { value: 'base', label: VISIT_SCOPE_LABELS.base },
                  { value: 'withOptions', label: VISIT_SCOPE_LABELS.withOptions },
                ]}
              />
            </div>
          )}
        <MarginPanel
          block={block}
          targetPct={targetPct}
          density={phone ? 'sheet' : 'popover'}
          lowest={lowest}
          showLowest={lowest.length > 0 || visitUnder}
        />
      </div>
    </EditorShell>
  )
}
